import { BlurTargetView as BlurTargetViewFromExpoBlur } from "@-/com.npmjs.expo-blur";
import { NavigationContext } from "@-/com.npmjs.expo-router/react-navigation";
import { Platform, View, type ViewProps } from "@-/com.npmjs.react-native";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import {
  BackdropRegistrationContext,
  CapturedBackdropContext,
  EMPTY_CAPTURED_BACKDROPS,
  FrostedBackdropContext,
  type BackdropRef,
} from "#frosted-backdrop-contexts";

const BlurTargetView = withUniwind(BlurTargetViewFromExpoBlur);
/** Shared chrome outside the active screen can blur that screen's content. */
export function FrostedBackdropProvider({ children }: PropsWithChildren) {
  const entries = useRef(new Map<BackdropRef, number>());
  const [target, setTarget] = useState<BackdropRef | null>(null);
  const publish = useCallback(() => {
    let next: BackdropRef | null = null;
    let deepest = -1;
    for (const [candidate, depth] of entries.current) {
      if (depth >= deepest) {
        next = candidate;
        deepest = depth;
      }
    }
    setTarget(next);
  }, []);
  const register = useCallback(
    (ref: BackdropRef, depth: number) => {
      entries.current.set(ref, depth);
      publish();
      return () => {
        entries.current.delete(ref);
        publish();
      };
    },
    [publish],
  );
  const value = useMemo(() => ({ register, target }), [register, target]);
  return <FrostedBackdropContext value={value}>{children}</FrostedBackdropContext>;
}

/**
 * Capture content only: put floating chrome beside this target, never inside it.
 * Hidden tab scenes release their registration. Nested content targets take priority, so reading
 * tabs outside their content target can blur it even inside a larger page target.
 */
export function FrostedBackdropTarget({
  active = true,
  children,
  ...props
}: ViewProps & { active?: boolean }) {
  const registry = useContext(FrostedBackdropContext);
  const captured = useContext(CapturedBackdropContext);
  const canRegister = useContext(BackdropRegistrationContext);
  const navigation = useContext(NavigationContext);
  const isFocused = useIsNavigationFocused();
  const target = useRef<View | null>(null);
  let depth = captured.length;
  let parent = navigation?.getParent();
  while (parent !== undefined && depth < 32) {
    depth += 1;
    parent = parent.getParent();
  }
  const register = registry?.register;
  const captures = Platform.OS === "android" && canRegister;
  const capturedTargets = useMemo(() => [...captured, target], [captured]);
  useEffect(() => {
    if (active && captures && isFocused && register !== undefined) {
      return register(target, depth);
    }
  }, [active, captures, depth, isFocused, register]);

  if (!captures) {
    return <View {...props}>{children}</View>;
  }
  return (
    <BlurTargetView {...props} ref={target}>
      <CapturedBackdropContext value={capturedTargets}>{children}</CapturedBackdropContext>
    </BlurTargetView>
  );
}

/** A native modal/portal is outside the captured view even though it retains React context. */
export function FrostedBackdropOverlay({ children }: PropsWithChildren) {
  return (
    <BackdropRegistrationContext value={false}>
      <CapturedBackdropContext value={EMPTY_CAPTURED_BACKDROPS}>{children}</CapturedBackdropContext>
    </BackdropRegistrationContext>
  );
}
